<template>
  <div class="google-integration-layout">
    <!-- Sidebar -->
    <Sidebar />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">

        <div class="page-header">
          <div class="page-header-left">
            <div class="page-icon">
              <img src="@/assets/google-color.svg" style="width:28px;height:28px" alt="Google" />
            </div>
            <div>
              <h1 class="page-title">Google Integrations</h1>
              <p class="page-subtitle">Connect your Google accounts to use Sheets, Drive &amp; Docs nodes in your WhatsApp flows.</p>
            </div>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>Loading connected accounts...</p>
        </div>

        <div v-else class="connections-section">

          <!-- Info banner -->
          <div class="info-banner">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            <div>
              <strong>How it works</strong>
              <p>Each Google account you connect becomes available inside Google Sheets, Drive, and Docs nodes in the Flow Builder. We only request the minimum scopes needed. You can disconnect at any time.</p>
            </div>
          </div>

          <!-- Connected accounts -->
          <div class="section-card">
            <div class="section-card-header">
              <h2>Connected Accounts</h2>
              <button v-if="connections.length === 0" @click="connectGoogle" class="btn-connect-google" :disabled="connecting">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                {{ connecting ? 'Redirecting to Google...' : 'Connect Google Account' }}
              </button>
            </div>

            <div v-if="connections.length === 0" class="empty-state">
              <div class="empty-icon">
                <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="2" y1="12" x2="22" y2="12"/>
                  <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
                </svg>
              </div>
              <h3>No Google accounts connected yet</h3>
              <p>Click <strong>Connect Google Account</strong> to authorize access to your Google Sheets, Drive, and Docs.</p>
            </div>

            <div v-else class="connections-list">
              <div v-for="conn in connections" :key="conn.id" class="connection-item">
                <div class="connection-avatar">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                </div>
                <div class="connection-info">
                  <div class="connection-email">{{ conn.email }}</div>
                  <div v-if="conn.display_name && conn.display_name !== conn.email" class="connection-name">{{ conn.display_name }}</div>
                  <div class="connection-scopes">
                    <span v-if="conn.scopes && conn.scopes.includes('spreadsheets')" class="scope-badge scope-sheets">Sheets</span>
                    <span v-if="conn.scopes && conn.scopes.includes('drive')" class="scope-badge scope-drive">Drive</span>
                    <span v-if="conn.scopes && conn.scopes.includes('documents')" class="scope-badge scope-docs">Docs</span>
                  </div>
                  <div class="connection-date">Connected {{ formatDate(conn.created_at) }}</div>
                </div>
                <div class="connection-actions">
                  <button
                    @click="disconnectConfirm(conn)"
                    class="btn-disconnect"
                    :disabled="disconnecting === conn.id"
                  >
                    {{ disconnecting === conn.id ? 'Disconnecting...' : 'Disconnect' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Scopes explanation -->
          <div class="section-card">
            <h2>What permissions are requested?</h2>
            <div class="scopes-list">
              <div class="scope-item">
                <div class="scope-icon scope-icon-sheets">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/></svg>
                </div>
                <div>
                  <strong>Google Sheets</strong>
                  <p>Read and write spreadsheet data — used by Read Rows, Append Row, and Update Row nodes.</p>
                </div>
              </div>
              <div class="scope-item">
                <div class="scope-icon scope-icon-drive">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>
                </div>
                <div>
                  <strong>Google Drive</strong>
                  <p>Upload files and create folders — used by Upload File and Create Folder nodes.</p>
                </div>
              </div>
              <div class="scope-item">
                <div class="scope-icon scope-icon-docs">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                </div>
                <div>
                  <strong>Google Docs</strong>
                  <p>Create and edit documents — used by Create Doc and Append Text nodes.</p>
                </div>
              </div>
            </div>
            <div class="privacy-note">
              We store your tokens securely and only use them to execute the actions you configure in your flows.
              Read our <a href="https://quali-d.com/privacy-policy" target="_blank" rel="noopener">Privacy Policy</a>.
            </div>
          </div>

        </div>

        <!-- Disconnect Confirm Modal -->
        <div v-if="showDisconnectModal" class="modal-overlay" @click="showDisconnectModal = false">
          <div class="modal-content" @click.stop>
            <div class="modal-header">
              <h3>Disconnect Google Account</h3>
              <button @click="showDisconnectModal = false" class="btn-close">×</button>
            </div>
            <div class="modal-body">
              <p>Are you sure you want to disconnect <strong>{{ pendingDisconnect?.email }}</strong>?</p>
              <p class="modal-warning">Any flow nodes using this account will stop working until you reconnect.</p>
            </div>
            <div class="modal-footer">
              <button @click="showDisconnectModal = false" class="btn-secondary">Cancel</button>
              <button @click="confirmDisconnect" class="btn-danger" :disabled="!!disconnecting">
                {{ disconnecting ? 'Disconnecting...' : 'Disconnect' }}
              </button>
            </div>
          </div>
        </div>

      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { DashboardService } from '@/services/dashboard'

export default {
  name: 'GoogleIntegrationView',
  components: { Sidebar, TopHeader },

  data () {
    return {
      loading: true,
      connections: [],
      connecting: false,
      disconnecting: null,
      showDisconnectModal: false,
      pendingDisconnect: null
    }
  },

  mounted () {
    this.loadConnections()
    this.handleOAuthCallback()
  },

  methods: {
    async loadConnections () {
      this.loading = true
      try {
        const resp = await DashboardService.getGoogleConnections()
        this.connections = resp.connections || []
      } catch (e) {
        this.$toast?.error('Failed to load Google connections')
      } finally {
        this.loading = false
      }
    },

    connectGoogle () {
      this.connecting = true
      const url = DashboardService.getGoogleOAuthUrl()
      window.location.href = url
    },

    disconnectConfirm (conn) {
      this.pendingDisconnect = conn
      this.showDisconnectModal = true
    },

    async confirmDisconnect () {
      if (!this.pendingDisconnect) return
      this.disconnecting = this.pendingDisconnect.id
      try {
        const resp = await DashboardService.disconnectGoogleConnection(this.pendingDisconnect.id)
        if (resp.success) {
          this.connections = this.connections.filter(c => c.id !== this.pendingDisconnect.id)
          this.$toast?.success('Google account disconnected')
        } else {
          this.$toast?.error(resp.message || 'Failed to disconnect')
        }
      } catch (e) {
        this.$toast?.error('Failed to disconnect')
      } finally {
        this.disconnecting = null
        this.showDisconnectModal = false
        this.pendingDisconnect = null
      }
    },

    handleOAuthCallback () {
      const params = new URLSearchParams(window.location.search)
      if (params.get('connected') === '1') {
        const email = params.get('email') || 'your Google account'
        this.$toast?.success(`Successfully connected ${email}`)
        window.history.replaceState({}, document.title, this.$route.path)
        // Reload to show the newly connected account
        this.loadConnections()
      }
      if (params.get('error')) {
        this.$toast?.error('Google connection failed: ' + params.get('error'))
        window.history.replaceState({}, document.title, this.$route.path)
      }
    },

    formatDate (dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    }
  }
}
</script>

<style scoped>
.google-integration-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-primary, #111827);
  color: var(--text-primary, #f9fafb);
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 280px;
}

.page-content {
  flex: 1;
  overflow-y: auto;
  padding: 32px;
}

/* Page Header */
.page-header {
  margin-bottom: 28px;
}

.page-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.page-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #4285F4, #34A853);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
}

.page-title {
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 4px;
  color: var(--text-primary, #f9fafb);
}

.page-subtitle {
  margin: 0;
  color: var(--text-secondary, #9ca3af);
  font-size: 14px;
}

/* Info banner */
.info-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: rgba(66, 133, 244, 0.08);
  border: 1px solid rgba(66, 133, 244, 0.2);
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 20px;
  font-size: 13px;
  color: var(--text-primary, #f9fafb);
}

.info-banner svg { flex-shrink: 0; margin-top: 1px; color: #4285F4; }
.info-banner strong { display: block; margin-bottom: 3px; font-size: 13px; }
.info-banner p { margin: 0; color: var(--text-secondary, #9ca3af); }

/* Section cards */
.section-card {
  background: var(--card-background, #1f2937);
  border: 1px solid var(--border-color, #374151);
  border-radius: 12px;
  padding: 22px 24px;
  margin-bottom: 20px;
  max-width: 820px;
}

.section-card h2 {
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 18px;
  color: var(--text-primary, #f9fafb);
}

.section-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.section-card-header h2 { margin: 0; }

/* Connect button */
.btn-connect-google {
  display: flex;
  align-items: center;
  gap: 7px;
  background: #4285F4;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
}

.btn-connect-google:hover:not(:disabled) { background: #3267d6; }
.btn-connect-google:disabled { opacity: 0.6; cursor: not-allowed; }

/* Empty state */
.empty-state {
  text-align: center;
  padding: 36px 20px;
  color: var(--text-secondary, #9ca3af);
}

.empty-icon { margin-bottom: 14px; opacity: 0.35; }
.empty-state h3 { font-size: 15px; font-weight: 600; margin: 0 0 7px; color: var(--text-primary, #f9fafb); }
.empty-state p { font-size: 13px; margin: 0; }

/* Connections list */
.connections-list { display: flex; flex-direction: column; gap: 10px; }

.connection-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: var(--bg-secondary, #111827);
  border: 1px solid var(--border-color, #374151);
  border-radius: 10px;
}

.connection-avatar {
  width: 42px;
  height: 42px;
  background: rgba(66, 133, 244, 0.12);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4285F4;
  flex-shrink: 0;
}

.connection-info { flex: 1; min-width: 0; }
.connection-email { font-size: 14px; font-weight: 600; color: var(--text-primary, #f9fafb); }
.connection-name { font-size: 12px; color: var(--text-secondary, #9ca3af); margin-top: 2px; }
.connection-date { font-size: 11px; color: var(--text-secondary, #6b7280); margin-top: 5px; }

.connection-scopes {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
  margin-top: 6px;
}

.scope-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.scope-sheets { background: rgba(52, 168, 83, 0.15); color: #34A853; }
.scope-drive  { background: rgba(66, 133, 244, 0.15); color: #4285F4; }
.scope-docs   { background: rgba(251, 188, 4, 0.15);  color: #FBBC04; }

.btn-disconnect {
  background: transparent;
  border: 1px solid var(--border-color, #374151);
  color: var(--text-secondary, #9ca3af);
  border-radius: 7px;
  padding: 6px 13px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}

.btn-disconnect:hover:not(:disabled) {
  border-color: #ef4444;
  color: #ef4444;
  background: rgba(239, 68, 68, 0.07);
}

.btn-disconnect:disabled { opacity: 0.5; cursor: not-allowed; }

/* Scopes explanation */
.scopes-list { display: flex; flex-direction: column; gap: 14px; margin-bottom: 18px; }

.scope-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.scope-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.scope-icon-sheets { background: rgba(52, 168, 83, 0.12); color: #34A853; }
.scope-icon-drive  { background: rgba(66, 133, 244, 0.12); color: #4285F4; }
.scope-icon-docs   { background: rgba(251, 188, 4, 0.12);  color: #FBBC04; }

.scope-item strong { display: block; font-size: 13px; font-weight: 600; margin-bottom: 2px; color: var(--text-primary, #f9fafb); }
.scope-item p { margin: 0; font-size: 12px; color: var(--text-secondary, #9ca3af); }

.privacy-note {
  font-size: 12px;
  color: var(--text-secondary, #6b7280);
  border-top: 1px solid var(--border-color, #374151);
  padding-top: 14px;
}

.privacy-note a { color: #4285F4; text-decoration: none; }
.privacy-note a:hover { text-decoration: underline; }

/* Loading */
.loading-state { text-align: center; padding: 60px 20px; color: var(--text-secondary, #9ca3af); }

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border-color, #374151);
  border-top-color: #4285F4;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 14px;
}

@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 768px) {
  .main-content { margin-left: 0; }
  .page-content { padding: 16px; }
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9000;
}

.modal-content {
  background: var(--card-background, #1f2937);
  border: 1px solid var(--border-color, #374151);
  border-radius: 12px;
  width: 100%;
  max-width: 400px;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  border-bottom: 1px solid var(--border-color, #374151);
}

.modal-header h3 { margin: 0; font-size: 15px; font-weight: 600; color: var(--text-primary, #f9fafb); }

.btn-close {
  background: none;
  border: none;
  font-size: 20px;
  line-height: 1;
  color: var(--text-secondary, #9ca3af);
  cursor: pointer;
  padding: 0;
}

.modal-body {
  padding: 18px 22px;
  font-size: 14px;
  color: var(--text-primary, #f9fafb);
}

.modal-body p { margin: 0 0 8px; }
.modal-body p:last-child { margin-bottom: 0; }
.modal-warning { color: #f59e0b; font-size: 13px; }

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 9px;
  padding: 14px 22px;
  border-top: 1px solid var(--border-color, #374151);
}

.btn-secondary {
  background: transparent;
  border: 1px solid var(--border-color, #374151);
  color: var(--text-primary, #f9fafb);
  border-radius: 7px;
  padding: 7px 15px;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-secondary:hover { background: rgba(255, 255, 255, 0.05); }

.btn-danger {
  background: #ef4444;
  border: none;
  color: white;
  border-radius: 7px;
  padding: 7px 15px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-danger:hover:not(:disabled) { background: #dc2626; }
.btn-danger:disabled { opacity: 0.6; cursor: not-allowed; }
</style>
